<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useQuery } from '@/composables/api.ts'
import { useUserExtendedData } from '@/composables/useUserExtendedData.ts'
import { useAuth } from '@/composables/useAuth.ts'
import { isUserToken } from '@/stores/authStore.ts'
import BDialog from '../../base/BDialog/BDialog.vue'
import AgreementSigningFlow from './AgreementSigningFlow.vue'
import type { Agreement, Creditor, Account } from './types'

const { t } = useI18n()

const { selectedToken, isAuthenticated, isSolicitor, isSystemUser } = useAuth()
const { data: userExtendedData, isLoading: isLoadingUserExtendedData } = useUserExtendedData()

const isApiUser = computed(() => {
  const payload = selectedToken.value?.payload
  if (!isUserToken(payload)) return false
  return payload.u_api_code != null
})

const { data: creditorsData, isLoading: isLoadingCreditors } = useQuery(
  '/creditors',
  { getAll: true, query: { projection: { _id: 1, account: 1, is_active: 1 } } },
  { enabled: computed(() => isAuthenticated.value && !isSolicitor.value && !isSystemUser.value) }
)

const { data: accountsData, isLoading: isLoadingAccounts } = useQuery(
  '/accounts',
  { getAll: true, query: { projection: { _id: 1, force_onboard_next_creditor: 1, enable_creditor_onboarding: 1 } } },
  { enabled: computed(() => isAuthenticated.value && !isSolicitor.value && !isSystemUser.value) }
)

const creditors = computed<Creditor[]>(() => creditorsData.value?._items ?? [])
const accounts = computed<Account[]>(() => accountsData.value?._items ?? [])

const accountMap = computed(() => {
  const map = new Map<string, Account>()
  for (const account of accounts.value) {
    map.set(account._id, account)
  }
  return map
})

const hasCreditorToOnboard = computed(() => {
  if (isApiUser.value || creditors.value.length === 0 || accounts.value.length === 0) {
    return false
  }

  for (const creditor of creditors.value) {
    const account = accountMap.value.get(creditor.account)
    if (account == null) continue

    if (account.force_onboard_next_creditor) return true
    if (!creditor.is_active && account.enable_creditor_onboarding) return true
  }

  return false
})

const agreementsToSign = computed<Agreement[]>(() => userExtendedData.value?.data_to_sign?.agreements ?? [])

const isLoading = computed(() => isLoadingUserExtendedData.value || isLoadingCreditors.value || isLoadingAccounts.value)

const shouldShowDialog = computed(() => {
  if (isLoading.value) return false
  return agreementsToSign.value.length > 0 && !hasCreditorToOnboard.value
})

const dialogOpen = ref(false)
const hasShownPopup = ref(false)

watch(
  [shouldShowDialog, isLoading],
  ([shouldShow, loading]) => {
    if (shouldShow && !loading && !hasShownPopup.value) {
      dialogOpen.value = true
      hasShownPopup.value = true
    }
  },
  { immediate: true }
)

const dialogTitle = computed(() => t('common.sign_agreement'))
</script>

<template>
  <b-dialog
    v-if="shouldShowDialog"
    v-model="dialogOpen"
    persistent
    size="medium"
    hide-footer
    :aria-label="dialogTitle"
  >
    <template #title>
      {{ dialogTitle }}
    </template>

    <agreement-signing-flow :agreements="agreementsToSign" />
  </b-dialog>
</template>
